import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Row, TextField, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { UploadedPdfRow } from '~/components/UploadedPdfRow/UploadedPdfRow';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useFilePicker } from '~/hooks/useFilePicker';
import { usePreviewCustomPagePdf } from '~/hooks/usePreviewCustomPagePdf';
import { useUploadCustomPagePdf } from '~/hooks/useUploadCustomPagePdf';
import { useLocaleKeys } from '~/i18n';
import { pageTitleValidation } from '~/models/custom-page-template/pageTitleValidation';
import { templateNameValidation } from '~/models/custom-page-template/templateNameValidation';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import s from './PdfTemplateFormModal.module.scss';
export const GetPdfTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
export const PdfTemplateForm = (param)=>{
    let { mode, template, initialUploadedFile, onBack, onSaved } = param;
    var _ref;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetPdfTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const [pdfFile, setPdfFile] = useState(()=>{
        if (mode === 'edit' && template) {
            return {
                pdfName: template.pdf.pdfName,
                pdfFileName: template.pdf.pdfFileName
            };
        }
        return initialUploadedFile;
    });
    const [fileError, setFileError] = useState(undefined);
    const { control, handleSubmit, setError, setValue, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            name: mode === 'edit' && template ? template.name : '',
            pageTitle: mode === 'edit' && template ? template.pageTitle : '',
            displayScope: mode === 'edit' && template ? template.displayScope : 'proposalOnly'
        }
    });
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError: (message)=>setError('name', {
                message
            })
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const handleUploadSuccess = useCallback((fileInfo)=>{
        setPdfFile(fileInfo);
        setFileError(undefined);
    }, []);
    const { handleFileSelected, isUploading } = useUploadCustomPagePdf({
        onSuccess: handleUploadSuccess,
        fileSizeErrorTestId: testIds.PdfTemplateFormModalTestIds.FileSizeErrorToast(),
        uploadErrorTestId: testIds.PdfTemplateFormModalTestIds.UploadErrorToast()
    });
    const { openFilePicker, fileInputElement } = useFilePicker({
        accept: '.pdf',
        onFileSelected: handleFileSelected,
        testId: testIds.PdfTemplateFormModalTestIds.FileInput()
    });
    const { openPreview } = usePreviewCustomPagePdf({
        biName: 'pdf_template_form_preview_modal'
    });
    const displayScope = watch('displayScope');
    const handlePreview = useCallback(()=>{
        if (pdfFile == null) return;
        openPreview(pdfFile.pdfName);
    }, [
        pdfFile,
        openPreview
    ]);
    const onSave = ()=>{
        if (isLoading || isUploading) return;
        if (pdfFile == null) {
            setFileError(t.customPages.templates.fileUploadRequired());
        }
        handleSubmit((values)=>{
            if (pdfFile == null) return;
            const payload = {
                type: 'pdf',
                name: values.name.trim(),
                pageTitle: values.pageTitle.trim(),
                displayScope: values.displayScope,
                pdf: {
                    pdfName: pdfFile.pdfName,
                    pdfFileName: pdfFile.pdfFileName
                }
            };
            if (mode === 'create') {
                createTemplate({
                    businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                    data: payload
                }, {
                    onError: handleSaveError
                });
            } else if (template) {
                updateTemplate({
                    businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                    templateId: template.id,
                    data: payload
                }, {
                    onError: handleSaveError
                });
            }
        })();
    };
    const toggleDisplayScope = ()=>{
        setValue('displayScope', displayScope === 'proposalOnly' ? 'proposalAndAgreement' : 'proposalOnly');
    };
    const hasFile = pdfFile != null || isUploading;
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.PdfTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(Controller, {
                name: "name",
                control: control,
                rules: templateNameValidation({
                    t: t.customPages.templates,
                    existingTemplates,
                    excludeTemplateId: template === null || template === void 0 ? void 0 : template.id
                }),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.PdfTemplateFormModalTestIds.NameField(),
                        label: t.customPages.templates.nameLabel(),
                        bi: {
                            name: 'pdf_template_form_name'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(Controller, {
                name: "pageTitle",
                control: control,
                rules: pageTitleValidation(t.customPages),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.PdfTemplateFormModalTestIds.PageTitleField(),
                        label: t.customPages.templates.pageTitleLabel(),
                        bi: {
                            name: 'pdf_template_form_page_title'
                        }
                    }))
            }),
            fileInputElement,
            hasFile ? /*#__PURE__*/ _jsx(UploadedPdfRow, {
                testId: testIds.PdfTemplateFormModalTestIds.UploadedPdfRow(),
                pdfFileName: (_ref = pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.pdfFileName) !== null && _ref !== void 0 ? _ref : '',
                fileSizeBytes: pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.fileSizeBytes,
                onChangeFile: openFilePicker,
                onPreview: handlePreview,
                isUploading: isUploading
            }) : /*#__PURE__*/ _jsxs(Box, {
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        testId: testIds.PdfTemplateFormModalTestIds.UploadArea(),
                        className: "".concat(s.uploadArea).concat(fileError != null ? " ".concat(s.hasError) : ''),
                        onClick: openFilePicker,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                children: t.customPages.pdfUpload.dragOrClick()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                children: t.customPages.pdfUpload.sizeLimit()
                            })
                        ]
                    }),
                    fileError != null && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        className: s.uploadErrorText,
                        testId: testIds.PdfTemplateFormModalTestIds.UploadAreaError(),
                        children: fileError
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                testId: testIds.PdfTemplateFormModalTestIds.DisplayScopeCheckbox(),
                checked: displayScope === 'proposalAndAgreement',
                onChange: toggleDisplayScope
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.PdfTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'pdf_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading || isUploading,
                        onClick: onSave,
                        testId: testIds.PdfTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'pdf_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
